// src/components/dashboard/Visits.js

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import GppBadRoundedIcon from "@mui/icons-material/GppBadRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const Visits = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Top Filter Controls State
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("All");
  const [selectedDateFilter, setSelectedDateFilter] = useState("Today");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Search & Table State
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Unified Clinical Design System Palette
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const addGreen = "#057A44";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";

  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";
  const rowHoverBg = isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.04)";
  const tableHeaderBg = isDark ? "#162238" : "#F4F9F9";

  const [visitsData, setVisitsData] = useState([
    {
      visitNo: "VST-7081",
      patientName: "Alexander Wright",
      patientEmail: "a.wright@healthmail.com",
      provider: "Dr. Usman (Neurologist)",
      dos: "09/11/2026",
      community: "Metropolitan Homecare",
      faceVerified: true,
      faceRegistered: true,
    },
    {
      visitNo: "VST-7080",
      patientName: "Charlotte Bennett",
      patientEmail: "c.bennett@careconnect.io",
      provider: "Elena Rostova, RN",
      dos: "09/11/2026",
      community: "Highland Tele-Care",
      faceVerified: true,
      faceRegistered: true,
    },
    {
      visitNo: "VST-7079",
      patientName: "Lucas Miller",
      patientEmail: "lucas.miller@neuroez.com",
      provider: "Dr. Sarah Jenkins",
      dos: "09/11/2026",
      community: "St. Jude Outpatient",
      faceVerified: false,
      faceRegistered: true,
    },
    {
      visitNo: "VST-7078",
      patientName: "Sophia Anderson",
      patientEmail: "sophia.anderson@medlink.org",
      provider: "Marcus Aurelius, PT",
      dos: "09/10/2026",
      community: "Coastal Rehab Center",
      faceVerified: true,
      faceRegistered: true,
    },
    {
      visitNo: "VST-7077",
      patientName: "Oliver Davies",
      patientEmail: "o.davies@nhs-portal.co.uk",
      provider: "Dr. Usman (Neurologist)",
      dos: "09/09/2026",
      community: "Metropolitan Homecare",
      faceVerified: false,
      faceRegistered: false,
    },
    {
      visitNo: "VST-7076",
      patientName: "Emma Taylor",
      patientEmail: "emma.taylor@globalcare.us",
      provider: "Elena Rostova, RN",
      dos: "09/08/2026",
      community: "Highland Tele-Care",
      faceVerified: true,
      faceRegistered: true,
    },
  ]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleDelete = (visitNo) => {
    setVisitsData((prev) => prev.filter((v) => v.visitNo !== visitNo));
  };

  const filteredVisits = visitsData.filter((row) => {
    const q = searchQuery.toLowerCase();
    return (
      row.patientName.toLowerCase().includes(q) ||
      row.visitNo.toLowerCase().includes(q) ||
      row.provider.toLowerCase().includes(q) ||
      row.community.toLowerCase().includes(q)
    );
  });

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryTextColor,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        minWidth: "150px",
        p: 0.5,
        "& .MuiMenuItem-root": {
          borderRadius: "6px",
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. TOP VISIT CONTROLLER BAR */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "stretch", lg: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: "1.7rem" }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.35rem" },
                fontWeight: 800,
                color: primaryTextColor,
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
              }}
            >
              Visit Management
            </Typography>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: secondaryTextColor,
                fontWeight: 500,
                mt: 0.3,
              }}
            >
              Schedule, track, and manage clinical visits
            </Typography>
          </Box>
        </Box>

        {/* Right Side Filter Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.2,
            justifyContent: { xs: "flex-start", lg: "flex-end" },
          }}
        >
          {/* Status Filter */}
          <Button
            variant="outlined"
            onClick={(e) => setStatusAnchor(e.currentTarget)}
            startIcon={<CheckRoundedIcon sx={{ fontSize: "1rem !important" }} />}
            endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              minWidth: "110px",
              borderRadius: "6px",
              borderColor: tealButton,
              backgroundColor: controlBg,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              px: 1.8,
              "&:hover": {
                borderColor: tealMain,
                backgroundColor: "rgba(36, 94, 101, 0.04)",
              },
            }}
          >
            {selectedStatusFilter}
          </Button>

          <Menu
            anchorEl={statusAnchor}
            open={Boolean(statusAnchor)}
            onClose={() => setStatusAnchor(null)}
            {...menuProps}
          >
            {["All", "Completed", "In Progress", "Scheduled"].map((item) => (
              <MenuItem
                key={item}
                onClick={() => {
                  setSelectedStatusFilter(item);
                  setStatusAnchor(null);
                }}
                sx={{
                  fontWeight: selectedStatusFilter === item ? 700 : 500,
                  backgroundColor:
                    selectedStatusFilter === item
                      ? isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg
                      : "transparent",
                  color: selectedStatusFilter === item ? tealButton : primaryTextColor,
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>

          {/* Date Filter */}
          <Button
            variant="outlined"
            onClick={(e) => setDateAnchor(e.currentTarget)}
            startIcon={<CalendarMonthRoundedIcon sx={{ fontSize: "1rem !important" }} />}
            endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              minWidth: "115px",
              borderRadius: "6px",
              borderColor: tealButton,
              backgroundColor: controlBg,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              px: 1.8,
              "&:hover": {
                borderColor: tealMain,
                backgroundColor: "rgba(36, 94, 101, 0.04)",
              },
            }}
          >
            {selectedDateFilter}
          </Button>

          <Menu
            anchorEl={dateAnchor}
            open={Boolean(dateAnchor)}
            onClose={() => setDateAnchor(null)}
            {...menuProps}
          >
            {["Today", "Yesterday", "This Week", "Custom Date"].map((item) => (
              <MenuItem
                key={item}
                onClick={() => {
                  setSelectedDateFilter(item);
                  setDateAnchor(null);
                }}
                sx={{
                  fontWeight: selectedDateFilter === item ? 700 : 500,
                  backgroundColor:
                    selectedDateFilter === item
                      ? isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg
                      : "transparent",
                  color: selectedDateFilter === item ? tealButton : primaryTextColor,
                }}
              >
                {item}
              </MenuItem>
            ))}
          </Menu>

          {/* Refresh Button */}
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={
              <RefreshRoundedIcon
                sx={{
                  fontSize: "1.15rem !important",
                  transform: isRefreshing ? "rotate(360deg)" : "none",
                  transition: "transform 0.5s ease-in-out",
                }}
              />
            }
            sx={{
              height: "40px",
              backgroundColor: tealButton,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.2,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#1c4a50",
                boxShadow: "none",
              },
            }}
          >
            Refresh
          </Button>

          {/* New Visit Button */}
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon sx={{ fontSize: "1.15rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: addGreen,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.82rem",
              borderRadius: "6px",
              px: 2.4,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#046337",
                boxShadow: "none",
              },
            }}
          >
            New Visit
          </Button>
        </Box>
      </Box>

      {/* 2. VISITS METRICS & SEARCH SECTION */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "24px 26px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SpaceDashboardRoundedIcon sx={{ fontSize: "1.15rem" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.98rem",
              fontWeight: 800,
              color: primaryTextColor,
              letterSpacing: "-0.2px",
            }}
          >
            Visits Dashboard
          </Typography>
        </Box>

        {/* 3 Unified Clinical Metric Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Total Visits */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: "14px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
              display: "flex",
              alignItems: "center",
              gap: 1.8,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                backgroundColor: pastelTealBg,
                color: tealButton,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: "1.4rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
                Total Visits
              </Typography>
              <Typography sx={{ fontSize: "1.45rem", fontWeight: 800, color: primaryTextColor }}>
                {visitsData.length}
              </Typography>
            </Box>
          </Box>

          {/* Completed */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: "14px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
              display: "flex",
              alignItems: "center",
              gap: 1.8,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7",
                color: isDark ? "#34D399" : "#15803D",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DoneAllRoundedIcon sx={{ fontSize: "1.4rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
                Completed Visits
              </Typography>
              <Typography sx={{ fontSize: "1.45rem", fontWeight: 800, color: primaryTextColor }}>
                {visitsData.filter((v) => v.faceVerified).length}
              </Typography>
            </Box>
          </Box>

          {/* Alert Unresolved */}
          <Box
            sx={{
              p: 2.2,
              borderRadius: "14px",
              backgroundColor: isDark ? "#162238" : "#F8FAFC",
              border: `1px solid ${softCardBorder}`,
              display: "flex",
              alignItems: "center",
              gap: 1.8,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(239, 68, 68, 0.15)" : "#FEE2E2",
                color: isDark ? "#F87171" : "#B91C1C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: "1.4rem" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>
                Alert Unresolved
              </Typography>
              <Typography sx={{ fontSize: "1.45rem", fontWeight: 800, color: primaryTextColor }}>
                {visitsData.filter((v) => !v.faceVerified).length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Search Input Bar */}
        <Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by patient name, provider, visit id, or community..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: secondaryTextColor, fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: controlBg,
                fontSize: "0.85rem",
                color: primaryTextColor,
                "& fieldset": { borderColor: controlBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
              },
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.2 }}>
            <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: secondaryTextColor }}>
              Results:
            </Typography>
            <Chip
              label={`${filteredVisits.length} visits`}
              size="small"
              sx={{
                height: "20px",
                fontSize: "0.68rem",
                fontWeight: 800,
                borderRadius: "6px",
                backgroundColor: pastelTealBg,
                color: tealButton,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* 3. VISIT RECORDS TABLE */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "16px",
          border: `1px solid ${softCardBorder}`,
          boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            py: 1.8,
            px: 2.8,
            borderBottom: `1px solid ${softCardBorder}`,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              backgroundColor: pastelTealBg,
              color: tealButton,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: "1.05rem" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.92rem",
              fontWeight: 800,
              color: primaryTextColor,
              letterSpacing: "-0.2px",
            }}
          >
            Visit Records
          </Typography>
        </Box>

        <TableContainer
          sx={{
            maxHeight: "560px",
            overflowY: "auto",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1050 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: tealButton,
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    backgroundColor: tableHeaderBg,
                    borderBottom: `1px solid ${softCardBorder}`,
                    py: 1.5,
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                  },
                }}
              >
                <TableCell>Visit #</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Patient
                    <ArrowUpwardRoundedIcon sx={{ fontSize: "0.85rem", color: tealButton }} />
                  </Box>
                </TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>DOS</TableCell>
                <TableCell>Community</TableCell>
                <TableCell>Face Verified</TableCell>
                <TableCell>Face Registered</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredVisits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ color: secondaryTextColor, fontSize: "0.88rem", fontWeight: 600 }}>
                      No Visits Found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVisits
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow
                      key={row.visitNo}
                      sx={{
                        transition: "background-color 0.15s ease",
                        "&:hover": { backgroundColor: rowHoverBg },
                        "& td": {
                          borderBottom: `1px solid ${
                            isDark ? "rgba(255, 255, 255, 0.04)" : "#F1F5F9"
                          }`,
                          py: 1.4,
                        },
                      }}
                    >
                      {/* Visit Number Badge */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            backgroundColor: pastelTealBg,
                            color: tealButton,
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {row.visitNo}
                        </Box>
                      </TableCell>

                      {/* Patient Info */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              backgroundColor: tealButton,
                              color: "#FFFFFF",
                            }}
                          >
                            {row.patientName[0]}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: primaryTextColor, fontSize: "0.84rem" }}>
                              {row.patientName}
                            </Typography>
                            <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor, fontWeight: 500 }}>
                              {row.patientEmail}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Provider */}
                      <TableCell sx={{ color: primaryTextColor, fontSize: "0.82rem", fontWeight: 600 }}>
                        {row.provider}
                      </TableCell>

                      {/* DOS */}
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem" }}>
                        {row.dos}
                      </TableCell>

                      {/* Community */}
                      <TableCell sx={{ color: secondaryTextColor, fontSize: "0.82rem", fontWeight: 500 }}>
                        {row.community}
                      </TableCell>

                      {/* Face Verified */}
                      <TableCell>
                        <Chip
                          icon={
                            row.faceVerified ? (
                              <VerifiedUserRoundedIcon sx={{ fontSize: "0.9rem !important", color: "#15803D !important" }} />
                            ) : (
                              <GppBadRoundedIcon sx={{ fontSize: "0.9rem !important", color: "#B91C1C !important" }} />
                            )
                          }
                          label={row.faceVerified ? "Verified" : "Unverified"}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            backgroundColor: row.faceVerified
                              ? isDark ? "rgba(16, 185, 129, 0.15)" : "#DCFCE7"
                              : isDark ? "rgba(239, 68, 68, 0.15)" : "#FEE2E2",
                            color: row.faceVerified ? (isDark ? "#34D399" : "#15803D") : (isDark ? "#F87171" : "#B91C1C"),
                          }}
                        />
                      </TableCell>

                      {/* Face Registered */}
                      <TableCell>
                        <Chip
                          label={row.faceRegistered ? "Enrolled" : "Not Enrolled"}
                          size="small"
                          sx={{
                            height: "22px",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            backgroundColor: row.faceRegistered ? pastelTealBg : (isDark ? "rgba(255,255,255,0.05)" : "#F1F5F9"),
                            color: row.faceRegistered ? tealButton : secondaryTextColor,
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3 }}>
                          <Tooltip title="View Encounter">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                "&:hover": { color: tealMain },
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Details">
                            <IconButton
                              size="small"
                              sx={{
                                color: secondaryTextColor,
                                "&:hover": { color: tealButton },
                              }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Visit">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(row.visitNo)}
                              sx={{
                                color: "#EF4444",
                                "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
                              }}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Bar */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredVisits.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: `1px solid ${softCardBorder}`,
            color: secondaryTextColor,
            "& .MuiTablePagination-select": {
              color: primaryTextColor,
              fontWeight: 600,
            },
            "& .MuiTablePagination-selectIcon": {
              color: secondaryTextColor,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Visits;